import { useEffect, useId, useRef } from "react";
import { ExternalLink, X } from "lucide-react";
import type { HousePromotionView } from "./housePromotions";

export function HousePromotionDialog(props: {
  promotion: HousePromotionView;
  label: string;
  closeLabel: string;
  continueLabel: string;
  onClose: () => void;
  onShown: () => void;
}) {
  const titleId = useId();
  const section = useRef<HTMLElement>(null);
  const continueButton = useRef<HTMLButtonElement>(null);
  const marked = useRef(false);

  useEffect(() => {
    const dialog = section.current;
    if (!dialog) return;
    const previousFocus = document.activeElement;
    const backdrop = dialog.parentElement;
    const siblings = Array.from(backdrop?.parentElement?.children || [])
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== backdrop);
    const previousInert = siblings.map((element) => element.inert);
    siblings.forEach((element) => { element.inert = true; });
    continueButton.current?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        props.onClose();
      } else if (event.key === "Tab") {
        const controls = Array.from(dialog.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex="0"]'));
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first?.focus();
        }
      }
    };
    dialog.addEventListener("keydown", onKeyDown);
    return () => {
      dialog.removeEventListener("keydown", onKeyDown);
      siblings.forEach((element, index) => { element.inert = previousInert[index]; });
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected && previousFocus.getClientRects().length) {
        previousFocus.focus({ preventScroll: true });
      } else {
        document.querySelector<HTMLElement>(".workspace-content button:not([disabled])")?.focus({ preventScroll: true });
      }
    };
  }, [props.onClose]);

  const promotion = props.promotion;
  return (
    <div className="dialog-backdrop house-promo-backdrop" role="presentation">
      <section ref={section} className="audio-access-dialog audio-access-dialog--house-promo" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="house-promo-header">
          <span className="house-promo-label">{props.label}</span>
          <button type="button" className="icon-button dialog-close" aria-label={props.closeLabel} onClick={props.onClose}><X aria-hidden="true" /></button>
        </header>
        <div className="house-promo-content" tabIndex={0} role="region" aria-labelledby={titleId}>
          <picture className="house-promo-picture">
            {promotion.mobileImageSrc ? <source media="(max-width: 600px)" srcSet={promotion.mobileImageSrc} width={800} height={800} /> : null}
            <img className="house-promo-media" src={promotion.imageSrc} width={1600} height={700} alt={promotion.imageAlt} loading="lazy" decoding="async" onError={props.onClose} onLoad={() => {
              if (!marked.current) { marked.current = true; props.onShown(); }
            }} />
          </picture>
          <div className="house-promo-copy" lang={promotion.language} dir="auto">
            <span className="house-promo-product">{promotion.productName}</span>
            <h2 id={titleId}>{promotion.headline}</h2>
            <p>{promotion.description}</p>
          </div>
        </div>
        <footer className="house-promo-actions">
          <a className="primary-button" href={promotion.destinationUrl} lang={promotion.language} dir="auto" target="_blank" rel="noopener noreferrer">{promotion.cta}<ExternalLink aria-hidden="true" /></a>
          <button ref={continueButton} type="button" className="secondary-button" onClick={props.onClose}>{props.continueLabel}</button>
        </footer>
      </section>
    </div>
  );
}
